import { useState, useEffect, useContext, useCallback, useRef, Suspense } from "react";
import { Chip } from "@material-ui/core";
import { DynamicForm } from "../../../../@types/dynamicForm.types";
import { AuthContext, UIContext } from "../../../../contexts";
import { ADMIN_DASHBOARD_HEADER } from "../../../../constants/caption/dashboardHeader";
import DynamicReportEditableForm from "../../../shared/dynamicEditableForm/DynamicEditableForm";
import { api } from "../../../../utils/api";
import { Validation } from "../../../../@types/validation.types";
import { trackPromise } from "react-promise-tracker";
import SnackbarAPICall from "../../../shared/snackbar/SnackbarAPICall";
import { SnackbarProps } from "../../../../@types/snackbarAPI.types";

import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";

import { useEmployerRoute } from "../../../../hooks";
import { Group } from "../../../../@types/group.types";
import useRouteParam from "../../../../hooks/useRouteParam";
import ROLES from "../../../../constants/roles";

export type DateOfBirthTypes = {
	group_number: number | null;
	role: string;
	SSN: string;
	date_of_birth: string;
};

const chipMessage = "This functionality will update the Date of Birth for Employee/Dependent of specific group.";

const DateOfBirthUpdate = (): JSX.Element => {
	const [reportDetailsDynamicForm, setReportDetailsDynamicForm] = useState<DynamicForm>();
	const { setDashboardHeader } = useContext(UIContext);
	const isEmployerRoute = useEmployerRoute();
	const queryGroupNumber = useRouteParam("gn");
	const [selectedGroup, setSelectedGroup] = useState<Group>();
	const [reportFieldDetails, setReportFieldDetails] = useState<DateOfBirthTypes>({
		group_number: null,
		role: "",
		SSN: "",
		date_of_birth: ""
	});
	const { user } = useContext(AuthContext); // Extracting logged in user from central storage.
	const [assignedGroups, setAssignedGroups] = useState([]);
	const [validation, setValidation] = useState<Validation>({
		reportFieldDetails: {},
		status: "invalid"
	});
	const _validation = useRef<Validation>();
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});

	const handleValidation = useCallback(async () => {
		const { group_number, date_of_birth, role, SSN } = reportFieldDetails;

		_validation.current = {
			...validation,
			reportFieldDetails: {},
			status: "invalid"
		};
		let flag = true;

		// employee validation checking
		if (group_number === null) {
			_validation.current.reportFieldDetails["group_number"] = "Group is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (role.length === 0) {
			_validation.current.reportFieldDetails["role"] = "Role is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (SSN.length === 0) {
			_validation.current.reportFieldDetails["SSN"] = "SSN is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (date_of_birth.length === 0) {
			_validation.current.reportFieldDetails["date_of_birth"] = "Date of Birth is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [reportFieldDetails, validation]);

	const getAssignGroups = useCallback(async () => {
		const params = {
			user_id: user?._id
		};
		const response = await api.assignGroupsAndLocation.getAssignedGroupsAndLocations(params);

		if (response) {
			const _assignedGroups: any[] = [];
			response.groupsAndLocations.forEach((group: any) => {
				if (group.assigned_group_info) {
					_assignedGroups.push(group);
				}
			});

			setAssignedGroups(Object.assign([], _assignedGroups));
		}
	}, [user?._id]);

	const handleSelectGroup = useCallback(
		(queryGroupNumber: number) => {
			// console.log("Query Group Number", queryGroupNumber);

			assignedGroups.forEach((assignedGroup: Group) => {
				if (assignedGroup.group_number === queryGroupNumber) {
					setSelectedGroup(Object.assign({}, assignedGroup));
					setReportFieldDetails(
						Object.assign({}, reportFieldDetails, {
							group_number: queryGroupNumber
						})
					);
				}
			});
		},

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[assignedGroups]
	);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = event.target;

			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: value }));
		},
		[reportFieldDetails]
	);

	const handleDateChange = useCallback(
		(_date: MaterialUiPickersDate, name: string) => {
			const date = new Date(_date as Date);
			const parsedDate = date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate();
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: parsedDate }));
		},
		[reportFieldDetails]
	);

	const handleClearDateField = useCallback(
		(event: any, name: string) => {
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: null }));
		},
		[reportFieldDetails]
	);

	const handleGenerateReportSubmit = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			try {
				event.preventDefault();
				const validation = await handleValidation();
				if (validation === "valid") {
					// console.log("Date of Birth Update");
					const { group_number, role, SSN, date_of_birth } = reportFieldDetails;
					const dateOfBirth = new Date(date_of_birth);

					const dateOfBirthPayload: any = {
						group_number,
						role,
						SSN: SSN.replaceAll("-", ""),
						date_of_birth:
							dateOfBirth.getFullYear() + "-" + (dateOfBirth.getMonth() + 1) + "-" + dateOfBirth.getDate()
					};

					// console.log("Date of Birth Payload", dateOfBirthPayload);

					const response: any = await trackPromise(api.memberInfoUpdate.dateofBirth(dateOfBirthPayload));

					// console.log("Response", response);
					if (response) {
						// console.log("Response", response);
						alert("Date of Birth Updation Successful");
						window.location.reload();

						// setTimeout(() => {
						// 	setSnackbarAPICallProps(
						// 		Object.assign({}, snackbarAPICallProps, {
						// 			open: true,
						// 			message: "Employee Info Changes Report Fetched Successfully and File Downloaded!",
						// 			severity: "success"
						// 		})
						// 	);
						// }, 2000);
					}
				}
			} catch (error) {
				alert("Date of Birth Update Unsuccessful!");
				throw error;
			}
		},
		[handleValidation, reportFieldDetails]
	);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.date_of_birth_updation);
		getAssignGroups();
	}, [getAssignGroups, setDashboardHeader]);

	useEffect(() => {
		if (isEmployerRoute && queryGroupNumber) handleSelectGroup(queryGroupNumber as number);
	}, [isEmployerRoute, queryGroupNumber, handleSelectGroup]);

	useEffect(() => {
		if (reportFieldDetails.date_of_birth === null)
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { date_of_birth: "" }));
	}, [reportFieldDetails]);

	useEffect(() => {
		setReportDetailsDynamicForm(
			Object.assign(
				{},
				{
					"Date of Birth Update Generation": [
						{
							label: "Group",
							name: "group_number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value:
								isEmployerRoute && queryGroupNumber
									? selectedGroup?.name
									: reportFieldDetails.group_number,
							type: isEmployerRoute && queryGroupNumber ? "textfield" : "select",
							options: [...assignedGroups]
						},
						{
							label: "Member Role",
							name: "role",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value: reportFieldDetails.role,
							type: "select",
							options: [ROLES.employee, ROLES.dependent]
						},
						{
							label: "Member SSN",
							name: "SSN",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							placeholder: "Member SSN",
							value: reportFieldDetails.SSN,
							type: "textfield"
						},
						{
							label: "Date of Birth",
							name: "date_of_birth",
							onChange: (date: MaterialUiPickersDate, name: string) => handleDateChange(date, name),
							placeholder: "Enter date of birth",
							value: reportFieldDetails.date_of_birth,
							type: "date"
						}
					]
				}
			)
		);
	}, [
		assignedGroups,
		reportFieldDetails,
		handleChange,
		handleDateChange,
		isEmployerRoute,
		queryGroupNumber,
		selectedGroup?.name
	]);

	// console.log("reportFieldDetails", reportFieldDetails);

	return (
		<div className="census-report-container" id="census-report-container">
			<Suspense fallback={<div />}>
				<SnackbarAPICall snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<div className="chip-container" id="chip-container">
				<Chip className="chip-green" label={chipMessage} variant="outlined" />
			</div>
			<DynamicReportEditableForm
				reportDetailsDynamicForm={reportDetailsDynamicForm}
				// selectedPlanName={selectedPlan?.plan_name}
				handleClearDateField={handleClearDateField}
				validation={validation}
				handleGenerateReportSubmit={handleGenerateReportSubmit}
			/>
		</div>
	);
};

export default DateOfBirthUpdate;
